/**
 * Abstract class that encapsulate the appContent service in each PWA.
 * This is the service that holds the main content data.
 *
 * This class provide static methods that can be used to update the content of an object based on a property name.
 * Each of the subclasses extending this class need to implement the abstract methods because only they know how they store the data.
 */
export abstract class BuilderAppContent {
  constructor() {}

  abstract fieldChanged(propertyPath: string, propertyValue: any): void;

  abstract fieldsChanged(
    propertyArr: { propertyId: string; propertyValue: string }[]
  ): void;

  abstract listItemAdded(
    propertyPath: string,
    propertyValue: any,
    insertIndex: number
  ): void;

  abstract listItemMoved(
    propertyPath: string,
    startIndex: number,
    endIndex: number
  ): void;

  abstract checkIfPropertyHasValue(propertyPath: string): boolean;

  abstract removeProperty(propertyPath: string): void;

  abstract getPropertyValue(propertyPath: string): any;

  /**
   * Recursively update a property of the main content object, the path of the property is passed dynamically as a parameter.
   * @param propertyPath e.g. "certifications.0.name"
   * @param propertyValue e.g. "Gluten Free"
   */
  public static updateProperty(
    appContent: any,
    propertyPath: string,
    propertyValue: any
  ) {
    if (propertyPath) {
      const pathParts = propertyPath.split('.');
      let tmpObject = appContent;
      pathParts.forEach((part, i) => {
        if (i === pathParts.length - 1) {
          tmpObject[part] = propertyValue;
        } else {
          tmpObject = tmpObject[part];
        }
      });
      return appContent;
    }
  }

  /**
   * Recursively checks that a property has a value and is not null or empty.
   * @param propertyPath e.g. "certifications.0.name"
   */
  public static checkIfPropertyHasValue(appContent: any, propertyPath: string) {
    return !!this.getPropertyValue(appContent, propertyPath);
  }

  /**
   * Recursively get a property value.
   * @param propertyPath e.g. "certifications.0.name"
   */
  public static getPropertyValue(appContent: any, propertyPath: string) {
    if (propertyPath && appContent) {
      try {
        const pathParts = propertyPath.split('.');
        let tmpObject = appContent;
        for (let i = 0; i < pathParts.length; ++i) {
          const part = pathParts[i];
          if (i === pathParts.length - 1) {
            return tmpObject[part];
          } else {
            tmpObject = tmpObject[part];
          }
        }
        return null;
      } catch (error) {
        console.error(
          `Error trying to getting property value of the path ${propertyPath}`,
          error
        );
        throw error;
      }
    }
  }

  /**
   * Recursively removes property from an object.
   * @param propertyPath e.g. "productTrail"
   */
  public static removeProperty(appContent: any, propertyPath: string) {
    if (propertyPath) {
      const pathParts = propertyPath.split('.');
      let tmpObject = appContent;
      for (let i = 0; i < pathParts.length; ++i) {
        const part = pathParts[i];
        if (i === pathParts.length - 1) {
          // this is the property that needs to be changed
          if (Array.isArray(tmpObject)) {
            tmpObject.splice(+part, 1);
          } else {
            delete tmpObject[part];
          }
        } else {
          tmpObject = tmpObject[part];
        }
      }
    }
  }

  /**
   * Adds an element to an array at a certain position.
   * @param appContent
   * @param propertyPath
   * @param propertyValue
   * @param insertIndex
   */
  public static listItemAdded(
    appContent: any,
    propertyPath: string,
    propertyValue: any,
    insertIndex: number
  ): void {
    const array = BuilderAppContent.getPropertyValue(appContent, propertyPath);
    if (!array) {
      throw new Error(`Cannot find array for ${propertyPath}`);
    }
    if (!Array.isArray(array)) {
      throw new Error(`${propertyPath} is not an array`);
    }
    if (insertIndex > array.length) {
      throw new Error(
        `Cannot add item to array ${propertyPath} at pos ${insertIndex} because length is only ${array.length}`
      );
    }
    array.splice(insertIndex, 0, propertyValue);
  }

  /**
   * Moves an element in an array from the previousIndex to the newIndex.
   * @param appContent
   * @param propertyPath
   * @param previousIndex
   * @param newIndex
   */
  public static listItemMoved(
    appContent: any,
    propertyPath: string,
    previousIndex: number,
    newIndex: number
  ): void {
    const array = BuilderAppContent.getPropertyValue(appContent, propertyPath);
    if (!array) {
      throw new Error(`Cannot find array for ${propertyPath}`);
    }
    if (!Array.isArray(array)) {
      throw new Error(`${propertyPath} is not an array`);
    }
    if (previousIndex > array.length - 1 || newIndex > array.length) {
      throw new Error(
        `Cannot move item in array ${propertyPath} from pos ${previousIndex} to pos ${newIndex} because length is only ${array.length}`
      );
    }
    if (newIndex === array.length) {
      let k = newIndex - array.length + 1;
      while (k--) {
        array.push(undefined);
      }
    }
    array.splice(newIndex, 0, array.splice(previousIndex, 1)[0]);
  }
}
